import { Link, useLocation } from 'react-router-dom';
import {
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  listItemButtonClasses,
  listItemIconClasses,
  listItemTextClasses,
  styled,
  typographyClasses,
} from 'ui';

import { isRouteActive } from './Sidebar';

export type SidebarLinkProps = {
  activeHref?: string | null;
  label: string;
  href: string;
  icon?: React.ReactNode;
  indent?: boolean;
  isLegacy?: boolean;
  onNavigate?: () => void;
  title?: string;
  dense?: boolean;
};

const normalizeHref = (href: string) => href.replace(/\/+$/, '') || '/';

const Item = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'indent' })<
  ListItemButtonProps & { indent?: boolean }
>(({ theme, indent }) => ({
  [`&.${listItemButtonClasses.selected}`]: {
    [`> .${listItemIconClasses.root}`]: {
      color: theme.palette.layout[100],
    },
    [`:hover`]: {
      backgroundColor: theme.palette.primary[500],
    },
    [`& .${listItemTextClasses.root} .${typographyClasses.root}`]: {
      fontWeight: 'normal !important',
    },
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.layout[100],
  },
  borderRadius: 1,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  ...(indent && { pl: 4 }),
})) as typeof ListItemButton; // MUI recommended typecasting https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop

export const SidebarLink = ({
  activeHref,
  title,
  label,
  href,
  icon,
  indent = false,
  isLegacy = true,
  dense = true,
  onNavigate,
}: SidebarLinkProps) => {
  const { pathname } = useLocation();
  const isActive =
    activeHref !== null && activeHref !== undefined
      ? normalizeHref(activeHref) === normalizeHref(href)
      : isRouteActive(pathname, href);
  const handlePointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    onNavigate?.();
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNavigate?.();
    }
  };
  const commonProps = {
    dense: dense,
    indent: indent,
    onKeyDown: handleKeyDown,
    onPointerDown: handlePointerDown,
    selected: isActive,
  };

  const content = (
    <>
      {icon !== null && icon !== undefined && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText
        primary={label}
        slotProps={{
          primary: {
            sx: {
              fontWeight: isActive ? 'bold' : 'regular',
              textTransform: 'capitalize',
            },
          },
        }}
      />
    </>
  );

  if (!isLegacy) {
    return (
      <Item {...commonProps} component={Link} to={href} title={title}>
        {content}
      </Item>
    );
  }

  return (
    <Item {...commonProps} component="a" href={href} title={title}>
      {content}
    </Item>
  );
};
